import { useCallback, useState } from 'react';
import { useGeoMarkupStore } from '@/store';

/**
 * `useState`-shaped open-state for a floating Mantine Menu/Popover rendered over
 * the map, with map-interaction suppression baked into the close transition.
 *
 * Closing the menu via an outside tap would otherwise fall through to the map
 * canvas and open the "add artifact" context menu on mobile; suppressing one
 * interaction on close prevents that click-through.
 *
 * Drop-in for `useState(false)` — returns `[opened, setOpened]`. The component
 * MUST be controlled: spread `opened` and pass `setOpened` as the Mantine
 * `onChange` handler. Mantine fires `onChange` synchronously from its
 * document-level outside-tap handler, so the suppression flag is set before
 * MapLibre synthesises the `click` from `touchend` — the same proven timing the
 * `AddressSearchBar` relies on. (Uncontrolled Menus must be converted first.)
 */
export function useMapOverlayMenu(
  initialOpened = false
): [boolean, (next: boolean) => void] {
  const [opened, setOpenedState] = useState(initialOpened);
  const suppressInteraction = useGeoMarkupStore(
    (state) => state.ui.suppressInteraction
  );

  const setOpened = useCallback(
    (next: boolean) => {
      if (!next) {
        suppressInteraction();
      }
      setOpenedState(next);
    },
    [suppressInteraction]
  );

  return [opened, setOpened];
}
